// src/components/staffing/StaffingHub.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { COLOR_GOOD, COLOR_WARN, COLOR_POOR } from '../../utils/colors';
import { Logger } from '../../utils/logger';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Paper,
  Avatar,
  AvatarGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as ContinueIcon,
  Visibility as ViewIcon,
  Archive as ArchiveIcon,
  Group as TeamIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { CURRENT_DATE, formatDate } from '../../utils/constants';
import { API_BASE_URL } from '../../config';
import { apiFetch } from '../../utils/api';
import staffingService from '../../services/staffingService';
import { searchProjects } from '../../services/projectService';
import OpportunityNameLink from '../common/OpportunityNameLink';
import ClientNameLink from '../common/ClientNameLink';
import MetricCopyright from '../common/MetricCopyright';

const logger = new Logger('StaffingHub');

// Date formatting utility
const formatDateDisplay = (dateString) => {
  if (!dateString || dateString === 'Unknown' || dateString === 'TBD') {
    return 'TBD';
  }
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'TBD';
    }
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return 'TBD';
  }
};

/**
 * StaffingHub component displays all active and completed staffing efforts
 */
const StaffingHub = ({ 
  onStartNewStaffing,
  onContinueStaffing,
  onViewCompleted,
  onImportFromOpportunity
}) => {
  const [activeStaffing, setActiveStaffing] = useState([]);
  const [filteredStaffing, setFilteredStaffing] = useState([]);
  const [completedStaffing, setCompletedStaffing] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [probabilityFilter, setProbabilityFilter] = useState('all');
  const [reviFilter, setReviFilter] = useState('all');
  const [daysUntilCloseFilter, setDaysUntilCloseFilter] = useState('all');
  const [sortBy, setSortBy] = useState('deadline');
  const [searchQuery, setSearchQuery] = useState('');

  // Load real staffing data from APIs
  useEffect(() => {
    const loadStaffingData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check for cached data first
        const CACHE_KEY = 'staffingHub_data_v2';
        const CACHE_TTL = 60 * 60 * 1000; // 1 hour

        try {
          const cachedData = localStorage.getItem(CACHE_KEY);
          if (cachedData) {
            const { data, timestamp } = JSON.parse(cachedData);
            const age = Date.now() - timestamp;

            if (age < CACHE_TTL) {
              logger.debug(`✅ Using cached StaffingHub data (${Math.round(age / 1000)}s old)`);

              // Deserialize date strings back to Date objects
              const deserializeStaffing = (staffing) => ({
                ...staffing,
                deadline: new Date(staffing.deadline),
                lastActivity: staffing.lastActivity ? new Date(staffing.lastActivity) : null,
                completedDate: staffing.completedDate ? new Date(staffing.completedDate) : null
              });

              const activeStaffingWithDates = data.activeStaffing.map(deserializeStaffing);
              const completedStaffingWithDates = data.completedStaffing.map(deserializeStaffing);

              setActiveStaffing(activeStaffingWithDates);
              setFilteredStaffing(activeStaffingWithDates);
              setCompletedStaffing(completedStaffingWithDates);
              setIsLoading(false);
              return; // Exit early with cached data
            } else {
              logger.debug('⏰ Cached data is stale, refreshing...');
            }
          }
        } catch (cacheError) {
          logger.debug('No valid cache found, loading fresh data');
        }

        // Get team configurations instead of workflows (backend migration)
        logger.debug('Getting team configurations for progress calculation...');
        const teamConfigurations = {};
        
        // We'll fetch configurations for each opportunity individually
        // since there's no bulk endpoint for all configurations
        
        // Get pipeline opportunities that need staffing
        let pipelineOpportunities = [];
        try {
          // FIXED: Request all opportunities by setting a high limit to avoid pagination issues
          const response = await apiFetch(`pipeline?limit=200`);
          if (response.ok) {
            const pipelineData = await response.json();
            pipelineOpportunities = pipelineData.opportunities || [];
            logger.debug('Pipeline opportunities from API:', pipelineOpportunities);
            logger.debug(`🔍 PIPELINE DEBUG: Loaded ${pipelineOpportunities.length} total opportunities from API`);
          }
        } catch (error) {
          logger.error('Error loading pipeline opportunities:', error);
        }
        
        // Transform opportunities into active staffing format
        const activeStaffingData = [];
        
        // Filter for staffing-ready opportunities (NEGOTIATION or PROPOSAL stage + POV Score > 5)
        const staffingReadyOpportunities = pipelineOpportunities.filter(opp =>
          (opp.stage === 'NEGOTIATION' || opp.stage === 'PROPOSAL') && (opp.revi_score || 0) > 5
        );

        logger.debug(`🔍 STAFFING FILTER DEBUG: Found ${staffingReadyOpportunities.length} staffing-ready opportunities (NEGOTIATION/PROPOSAL + POV > 5)`);
        logger.debug(`   - NEGOTIATION: ${pipelineOpportunities.filter(o => o.stage === 'NEGOTIATION').length}, PROPOSAL: ${pipelineOpportunities.filter(o => o.stage === 'PROPOSAL').length}`);
        
        // Check for specific opportunities
        const opp049 = pipelineOpportunities.find(o => o.opportunity_id === 'OPP-2024-049');
        const opp050 = pipelineOpportunities.find(o => o.opportunity_id === 'OPP-2024-050');
        
        logger.debug(`🔍 SPECIFIC OPPORTUNITY CHECK:`);
        logger.debug(`   - OPP-2024-049 found: ${!!opp049}, stage: ${opp049?.stage || 'NOT_FOUND'}`);
        logger.debug(`   - OPP-2024-050 found: ${!!opp050}, stage: ${opp050?.stage || 'NOT_FOUND'}`);
        
        if (staffingReadyOpportunities && staffingReadyOpportunities.length > 0) {
          // Use all staffing-ready opportunities (no limit)
          const opportunitiesToUse = staffingReadyOpportunities;
          
          for (let i = 0; i < opportunitiesToUse.length; i++) {
            const opp = opportunitiesToUse[i];
            
            // Use real deadline from pipeline data
            const deadline = opp.estimated_close_date ? new Date(opp.estimated_close_date) : 
                             opp.expected_close_date ? new Date(opp.expected_close_date) :
                             new Date(CURRENT_DATE.getTime() + 60 * 24 * 60 * 60 * 1000); // fallback to 60 days
            
            const lastActivity = new Date(CURRENT_DATE.getTime() - 24 * 60 * 60 * 1000); // 1 day ago
            
            // Pursuit team vs staffing are different concepts
            const pursuitTeam = opp.technical_resources || [];
            const salesOwner = opp.sales_owner || {};
            
            // For staffing purposes, use ONLY database roles - ENTERPRISE: NO FALLBACKS
            const totalRoles = (opp.roles && Array.isArray(opp.roles)) ? opp.roles.length : 0;
            
            // Get actual staffing progress from team configurations
            let filledRoles = 0;
            let completionPercentage = 0;
            let configurationCount = 0;
            let hasSoftBooking = false;
            let softBookedConfig = null;

            // Get team configurations for this opportunity
            const opportunityId = opp.opportunity_id || opp.project_id;

            logger.debug('🔍 Fetching configurations for', opp.opportunity_name, 'ID:', opportunityId);

            try {
              const configResponse = await apiFetch(`team-optimization/configurations/project/${opportunityId}`);
              logger.debug('🔍 Config response status:', configResponse.status, 'for', opp.opportunity_name);

              if (configResponse.ok) {
                const configData = await configResponse.json();
                const configurations = configData.configurations || [];

                logger.debug('🔍 Configurations retrieved for', opp.opportunity_name, ':', configurations.length, 'configs');

                // Check for soft booked configurations
                softBookedConfig = configurations.find(c => c.booking_status === 'soft_booked');
                hasSoftBooking = !!softBookedConfig;
                configurationCount = configurations.length;

                logger.debug('🔍 Soft booking check for', opp.opportunity_name, ':', {
                  hasSoftBooking,
                  configCount: configurationCount,
                  bookingStatuses: configurations.map(c => c.booking_status)
                });
                
                if (configurations.length > 0) {
                  // Find the configuration with the most team members (most complete)
                  const mostCompleteConfig = configurations.reduce((max, config) => {
                    const teamSize = (config.team_members || []).length;
                    const maxTeamSize = (max.team_members || []).length;
                    return teamSize > maxTeamSize ? config : max;
                  }, configurations[0]);
                  
                  filledRoles = (mostCompleteConfig.team_members || []).length;
                  completionPercentage = totalRoles > 0 ? Math.round((filledRoles / totalRoles) * 100) :
                                        filledRoles > 0 ? 100 : 0; // If no roles defined but team exists, assume complete

                  logger.debug(`📊 TEAM CONFIG UPDATE for ${opp.opportunity_name}: ${filledRoles} team members, ${configurationCount} configs (${completionPercentage}% staffed)`);
                } else {
                  logger.debug(`📊 NO TEAM CONFIGS found for ${opp.opportunity_name} (${opportunityId})`);
                }
              }
            } catch (error) {
              logger.debug(`📊 ERROR fetching team configs for ${opp.opportunity_name}: ${error.message}`);
            }

            // Calculate if opportunity is "NEW" (first seen within 7 days of CURRENT_DATE)
            let isNew = false;
            if (opp.staffing_hub_first_seen_date) {
              const firstSeenDate = new Date(opp.staffing_hub_first_seen_date);
              const daysSinceFirstSeen = Math.floor((CURRENT_DATE - firstSeenDate) / (1000 * 60 * 60 * 24));
              isNew = daysSinceFirstSeen <= 7;

              logger.debug(`📊 NEW badge check for ${opp.opportunity_name}: first seen ${daysSinceFirstSeen} days ago, isNew: ${isNew}`);
            }

            const staffingProject = {
              id: opp.opportunity_id || opp.project_id || `staffing-${i}`,
              projectName: opp.opportunity_name || opp.name || `Project ${i + 1}`,
              clientName: opp.client_name || 'Unknown Client',
              projectType: 'opportunity',
              progress: {
                totalRoles: totalRoles,
                filledRoles: filledRoles,
                completionPercentage: completionPercentage
              },
              configurations: {
                count: configurationCount,
                hasSoftBooking: hasSoftBooking,
                softBookedConfigName: softBookedConfig?.configuration_name || null
              },
              isNew: isNew,
              priority: opp.stage === 'NEGOTIATION' ? 'high' : opp.stage === 'CLOSED WON' ? 'high' : 'medium',
              deadline: deadline,
              lastActivity: lastActivity,
              pursuitTeam: pursuitTeam.slice(0, 3).map(resource => ({
                name: resource.name || 'Unknown',
                avatar: '/avatars/default.jpg'
              })),
              salesOwner: salesOwner,
              status: opp.stage === 'NEGOTIATION' ? 'in_progress' : opp.stage === 'CLOSED WON' ? 'in_progress' : 'early_stage',
              urgentRoles: completionPercentage < 100 ? ['Additional Resources Needed'] : [],
              originalData: opp
            };

            // Debug logging for soft booking
            if (configurationCount > 0) {
              logger.debug('🔍 Staffing Hub - Configuration data for', opp.opportunity_name, ':', {
                configCount: configurationCount,
                hasSoftBooking: hasSoftBooking,
                softBookedConfigName: softBookedConfig?.configuration_name
              });
            }

            activeStaffingData.push(staffingProject);
          }
        }
        
        // If no opportunities found, create minimal demo data
        if (activeStaffingData.length === 0) {
          activeStaffingData.push({
            id: 'demo-project-1',
            projectName: 'Healthcare Systems Integration',
            clientName: 'Regional Medical Center',
            projectType: 'opportunity',
            progress: {
              totalRoles: 4,
              filledRoles: 2,
              completionPercentage: 50
            },
            priority: 'high',
            deadline: new Date(CURRENT_DATE.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days from current date
            lastActivity: new Date(CURRENT_DATE.getTime() - 24 * 60 * 60 * 1000), // 1 day ago
            assignedUsers: [
              { name: 'John Quirk', avatar: '/avatars/john.jpg' }
            ],
            status: 'in_progress',
            urgentRoles: ['Senior Architect', 'Healthcare Analyst']
          });
        }
        
        // Mock completed staffing for now - can be enhanced later
        const completedStaffingData = [
          {
            id: 'completed-project-1',
            projectName: 'Data Analytics Platform',
            clientName: 'Manufacturing Corp',
            completedDate: new Date(CURRENT_DATE.getTime() - 7 * 24 * 60 * 60 * 1000), // 1 week ago
            totalRoles: 4,
            successRate: 95
          }
        ];

        setActiveStaffing(activeStaffingData);
        setFilteredStaffing(activeStaffingData); // Initialize filtered data
        setCompletedStaffing(completedStaffingData);

        // Cache the loaded data
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: {
              activeStaffing: activeStaffingData,
              completedStaffing: completedStaffingData
            },
            timestamp: Date.now()
          }));
          logger.debug('✅ Cached StaffingHub data');
        } catch (cacheError) {
          logger.warn('Failed to cache data:', cacheError);
        }

        setIsLoading(false);
      } catch (err) {
        logger.error('Error loading staffing data:', err);
        setError('Failed to load staffing data: ' + (err.message || 'Unknown error'));
        setIsLoading(false);
      }
    };

    loadStaffingData();
  }, []);

  // Filter and sort effect
  useEffect(() => {
    let filtered = [...activeStaffing];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(staffing => {
        // Search in multiple fields
        const searchableText = [
          staffing.projectName,
          staffing.clientName,
          staffing.id,
          staffing.originalData?.stage,
          staffing.originalData?.opportunity_name,
          staffing.originalData?.client_name,
          staffing.originalData?.opportunity_id,
          staffing.salesOwner?.employee_name,
          ...(staffing.pursuitTeam?.map(member => member.name) || [])
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(query);
      });
    }

    // Apply stage filter
    if (stageFilter !== 'all') {
      filtered = filtered.filter(staffing =>
        staffing.originalData?.stage === stageFilter
      );
    }

    // Apply status filter based on configuration status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(staffing => {
        const configCount = staffing.configurations?.count || 0;
        const hasSoftBooking = staffing.configurations?.hasSoftBooking || false;

        // Determine the status based on configuration
        if (statusFilter === 'new') {
          return staffing.isNew === true;
        } else if (statusFilter === 'soft_booked') {
          return hasSoftBooking;
        } else if (statusFilter === 'not_started') {
          return configCount === 0 && !hasSoftBooking;
        } else if (statusFilter === 'in_progress') {
          return configCount > 0 && !hasSoftBooking;
        }

        return false;
      });
    }

    // Apply probability filter
    if (probabilityFilter !== 'all') {
      filtered = filtered.filter(staffing => {
        // Try multiple ways to get the probability value
        let probability = 0;
        
        if (staffing.originalData?.win_probability) {
          probability = parseFloat(staffing.originalData.win_probability);
          // If it's a decimal (0.6), convert to percentage (60)
          if (probability <= 1) {
            probability = probability * 100;
          }
        } else if (staffing.originalData?.win_probability_percentage) {
          // Remove % sign if present and parse
          const probStr = staffing.originalData.win_probability_percentage.toString().replace('%', '');
          probability = parseFloat(probStr);
        }
        
        logger.debug('Filtering probability (converted to %):', probability, 'for', staffing.projectName);
        
        switch (probabilityFilter) {
          case 'high':
            return probability >= 70;
          case 'medium':
            return probability >= 50 && probability <= 69;
          case 'low':
            return probability <= 49;
          default:
            return true;
        }
      });
    }

    // Apply POV filter
    if (reviFilter !== 'all') {
      filtered = filtered.filter(staffing => {
        const reviScore = parseFloat(staffing.originalData?.revi_score || 0);

        logger.debug('Filtering POV score:', reviScore, 'for', staffing.projectName);
        
        switch (reviFilter) {
          case 'high':
            return reviScore >= 8.0;
          case 'medium':
            return reviScore >= 6.0 && reviScore <= 7.9;
          case 'low':
            return reviScore <= 5.9;
          default:
            return true;
        }
      });
    }

    // Apply Days Until Close filter
    if (daysUntilCloseFilter !== 'all') {
      filtered = filtered.filter(staffing => {
        const closeDate = new Date(staffing.deadline);
        const currentDate = new Date(CURRENT_DATE);
        const diffTime = closeDate.getTime() - currentDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        logger.debug('Filtering days until close:', diffDays, 'for', staffing.projectName);
        
        switch (daysUntilCloseFilter) {
          case 'under10':
            return diffDays <= 10;
          case 'under30':
            return diffDays <= 30;
          case 'under60':
            return diffDays <= 60;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          return new Date(a.deadline) - new Date(b.deadline); // Earliest deadline first
        case 'status':
          const statusOrder = { 'in_progress': 1, 'early_stage': 2, 'just_started': 3 };
          return statusOrder[a.status] - statusOrder[b.status];
        case 'winProbability':
          const aProb = parseFloat(a.originalData?.win_probability || 0);
          const bProb = parseFloat(b.originalData?.win_probability || 0);
          return bProb - aProb; // Highest probability first
        case 'reviScore':
          const aRevi = parseFloat(a.originalData?.revi_score || 0);
          const bRevi = parseFloat(b.originalData?.revi_score || 0);
          return bRevi - aRevi; // Highest POV score first
        default:
          return 0;
      }
    });

    setFilteredStaffing(filtered);
  }, [activeStaffing, statusFilter, stageFilter, probabilityFilter, reviFilter, daysUntilCloseFilter, sortBy, searchQuery]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return '#7b1fa2';
      case 'early_stage': return '#2196f3';
      case 'just_started': return COLOR_GOOD;
      default: return '#757575';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'early_stage': return 'Early Stage';
      case 'just_started': return 'Just Started';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return COLOR_POOR;
      case 'medium': return '#ffffff'; // White text for medium priority
      case 'low': return COLOR_GOOD;
      default: return '#757575';
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case 'high': return 'transparent';
      case 'medium': return '#7b1fa2'; // Purple background for medium priority
      case 'low': return 'transparent';
      default: return 'transparent';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Loading Staffing Hub...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          Staffing Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all your staffing efforts in one place
        </Typography>
      </Box>


      {/* Search Bar */}
      {activeStaffing.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search opportunities by name, client, ID, or stage..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#7b1fa2' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ color: '#7b1fa2' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#7b1fa2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7b1fa2',
                },
              },
            }}
          />
        </Box>
      )}

      {/* Filter and Sort Controls */}
      {activeStaffing.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon sx={{ color: '#7b1fa2' }} />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="new">NEW</MenuItem>
                  <MenuItem value="not_started">Not Started</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="soft_booked">Soft Booked</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Stage</InputLabel>
                <Select
                  value={stageFilter}
                  label="Stage"
                  onChange={(e) => setStageFilter(e.target.value)}
                >
                  <MenuItem value="all">All Stages</MenuItem>
                  <MenuItem value="NEGOTIATION">Negotiation</MenuItem>
                  <MenuItem value="PROPOSAL">Proposal</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>% Likely</InputLabel>
                <Select
                  value={probabilityFilter}
                  label="% Likely"
                  onChange={(e) => setProbabilityFilter(e.target.value)}
                >
                  <MenuItem value="all">All %</MenuItem>
                  <MenuItem value="high">≥70%</MenuItem>
                  <MenuItem value="medium">50-69%</MenuItem>
                  <MenuItem value="low">≤49%</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel><MetricCopyright name="POV" /></InputLabel>
                <Select
                  value={reviFilter}
                  label="POV©"
                  onChange={(e) => setReviFilter(e.target.value)}
                >
                  <MenuItem value="all">All <MetricCopyright name="POV" /></MenuItem>
                  <MenuItem value="high">≥8.0</MenuItem>
                  <MenuItem value="medium">6.0-7.9</MenuItem>
                  <MenuItem value="low">≤5.9</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Days to Close</InputLabel>
                <Select
                  value={daysUntilCloseFilter}
                  label="Days to Close"
                  onChange={(e) => setDaysUntilCloseFilter(e.target.value)}
                >
                  <MenuItem value="all">All Days</MenuItem>
                  <MenuItem value="under10">Under 10 Days</MenuItem>
                  <MenuItem value="under30">Under 30 Days</MenuItem>
                  <MenuItem value="under60">Under 60 Days</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SortIcon sx={{ color: '#7b1fa2' }} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="deadline">Days Until Close</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="winProbability">Win Probability</MenuItem>
                  <MenuItem value="reviScore"><MetricCopyright name="POV" /> Score</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Active Staffing */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <ScheduleIcon sx={{ mr: 1, color: '#7b1fa2' }} />
          Active Staffing ({filteredStaffing.length})
        </Typography>
        
        {activeStaffing.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No active staffing efforts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start your first staffing effort to see it here
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onStartNewStaffing}
              sx={{ 
                bgcolor: '#7b1fa2',
                color: 'white',
                '&:hover': { bgcolor: '#6a1b9a', color: 'white' }
              }}
            >
              Start New Staffing
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredStaffing.map((staffing) => (
              <Grid item xs={12} md={6} lg={4} key={staffing.id}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `2px solid ${staffing.isNew ? COLOR_GOOD : getStatusColor(staffing.status)}`,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          <OpportunityNameLink opportunity={{ opportunity_id: staffing.originalData?.opportunity_id || staffing.id, name: staffing.projectName, client_name: staffing.clientName }}>
                            {staffing.projectName}
                          </OpportunityNameLink>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <ClientNameLink client={{ client_id: staffing.originalData?.client_id, client_name: staffing.clientName }}>
                            {staffing.clientName}
                          </ClientNameLink>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ID: {staffing.id} | Stage: {staffing.originalData?.stage || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Close Date: {formatDateDisplay(staffing.originalData?.estimated_close_date)} | Start Date: {formatDateDisplay(staffing.originalData?.estimated_start_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Staffing Due Date: {formatDateDisplay(staffing.originalData?.estimated_close_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Win Probability: {staffing.originalData?.win_probability_percentage || 'Unknown'} | POV©: {staffing.originalData?.revi_score || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">
                          Deadline
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {(() => {
                            const closeDate = new Date(staffing.deadline);
                            const currentDate = new Date(CURRENT_DATE);
                            const diffTime = closeDate.getTime() - currentDate.getTime();
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            
                            if (diffDays > 0) {
                              return `${diffDays} days`;
                            } else if (diffDays === 0) {
                              return 'Today';
                            } else {
                              return `${Math.abs(diffDays)} days overdue`;
                            }
                          })()}
                        </Typography>
                        {/* NEW Badge */}
                        {staffing.isNew && (
                          <Chip
                            label="NEW"
                            size="small"
                            sx={{
                              bgcolor: COLOR_GOOD,
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.65rem',
                              height: '18px',
                              mt: 0.5
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Progress - Team Staffing */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Team Staffing Progress
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
                          {staffing.progress.totalRoles} Roles • {staffing.configurations?.count || 0} Team{staffing.configurations?.count === 1 ? '' : 's'}
                        </Typography>
                      </Box>

                      {/* Configurations Progress Bar */}
                      <Box sx={{ mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(() => {
                            const configCount = staffing.configurations?.count || 0;
                            // Assume 3 configurations is 100% progress (multiple scenarios explored)
                            return Math.min((configCount / 3) * 100, 100);
                          })()}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              bgcolor: staffing.configurations?.hasSoftBooking ? COLOR_GOOD : '#7b1fa2'
                            }
                          }}
                        />
                      </Box>

                      {/* Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {staffing.configurations?.hasSoftBooking ? (
                          <Chip
                            label="SOFT BOOKED"
                            size="small"
                            sx={{
                              bgcolor: COLOR_GOOD,
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              height: '20px'
                            }}
                          />
                        ) : (
                          <Chip
                            label={(() => {
                              const configCount = staffing.configurations?.count || 0;
                              if (configCount === 0) return 'NOT STARTED';
                              return 'IN PROGRESS';
                            })()}
                            size="small"
                            sx={{
                              bgcolor: (() => {
                                const configCount = staffing.configurations?.count || 0;
                                if (configCount === 0) return '#757575';
                                return '#7b1fa2';
                              })(),
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              height: '20px'
                            }}
                          />
                        )}
                      </Box>
                    </Box>


                    {/* Pursuit Team & Sales Person */}
                    <Box sx={{ mb: 2 }}>
                      {/* Sales Person */}
                      {staffing.salesOwner && staffing.salesOwner.employee_name && (
                        <Box sx={{ mb: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'white',
                              fontWeight: '600',
                              textDecoration: 'underline',
                              textDecorationColor: '#7b1fa2',
                              textUnderlineOffset: '2px'
                            }}
                          >
                            Sales Person
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block', mt: 0.5 }}>
                            {staffing.salesOwner.employee_name}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Pursuit Team */}
                      <Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textDecorationColor: '#7b1fa2',
                            textUnderlineOffset: '2px'
                          }}
                        >
                          Pursuit Team
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {staffing.pursuitTeam && staffing.pursuitTeam.length > 0 ? (
                            staffing.pursuitTeam.map((user, index) => (
                              <Typography key={index} variant="caption" sx={{ fontWeight: 'medium', display: 'block' }}>
                                {user.name}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No pursuit team assigned
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      startIcon={<ContinueIcon />}
                      onClick={() => onContinueStaffing(staffing.id, staffing)}
                      sx={{ 
                        flexGrow: 1,
                        bgcolor: '#7b1fa2',
                        color: 'white',
                        '&:hover': { bgcolor: '#6a1b9a', color: 'white' }
                      }}
                    >
                      Staffing Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

StaffingHub.propTypes = {
  onStartNewStaffing: PropTypes.func.isRequired,
  onContinueStaffing: PropTypes.func.isRequired,
  onViewCompleted: PropTypes.func.isRequired,
  onImportFromOpportunity: PropTypes.func.isRequired
};

export default StaffingHub;
